import { sameCanonical } from 'config-hub-server/src/model/canonicalJson.js';
import { isPlainObject } from './baseValues.js';
import { FIELDS, fieldOrder, fieldSpec, type FieldKind } from './fields.js';
import type { EntityType } from './labels.js';
import { valueText } from './valueText.js';

/**
 * A diff as a list of fields, never a document.
 *
 * Each top-level field is a group; its rows are the leaves that differ. `path[0]` is the field's
 * label and the rest are raw keys (a map's keys as written, a list of objects' positions as `#n`).
 * A list of scalars is one row of chips in the after side's order, then the items only the before
 * side had. Everything else is a scalar row with before and after.
 */
export type DiffRow =
    | {
          kind: 'scalar';
          path: string[];
          before: unknown;
          after: unknown;
          change: 'changed' | 'added' | 'removed' | 'unchanged';
      }
    | { kind: 'chips'; path: string[]; items: { value: string; side: 'both' | 'added' | 'removed' }[] };
export type DiffGroup = { field: string; label: string; kind: FieldKind; rows: DiffRow[] };

export type Pair = { field: string; before: unknown; after: unknown };

const isScalar = (v: unknown): boolean =>
    v === null || v instanceof Date || (typeof v !== 'object' && typeof v !== 'function');

const isScalarList = (v: unknown): v is unknown[] => Array.isArray(v) && v.every(isScalar);

const isUnset = (v: unknown): boolean => v === undefined || v === null || v === '';

/** A `Date` is not JSON — canonicalize it to its instant so `sameCanonical` can compare it structurally, recursively (an object or array may hold one at any depth). */
const dateSafe = (v: unknown): unknown => {
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? 'Invalid Date' : v.toISOString();
    if (Array.isArray(v)) return v.map(dateSafe);
    if (isPlainObject(v)) return Object.fromEntries(Object.entries(v).map(([key, value]) => [key, dateSafe(value)]));
    return v;
};

/** Structural equality: key order is ignored, a missing key equals an undefined one, null is its own value. */
export const sameValue = (a: unknown, b: unknown): boolean => sameCanonical(dateSafe(a), dateSafe(b));

const unionKeys = (before: Record<string, unknown>, after: Record<string, unknown>): string[] => {
    const keys = Object.keys(after);
    for (const key of Object.keys(before)) if (!(key in after)) keys.push(key);
    return keys;
};

const chipIdentity = (v: unknown): string => `${v instanceof Date ? 'date' : typeof v}:${valueText(v)}`;

const chipsRow = (path: string[], before: unknown[], after: unknown[]): DiffRow => {
    const remaining = new Map<string, number>();
    for (const item of before) remaining.set(chipIdentity(item), (remaining.get(chipIdentity(item)) ?? 0) + 1);
    const items: { value: string; side: 'both' | 'added' | 'removed' }[] = after.map((item) => {
        const id = chipIdentity(item);
        const left = remaining.get(id) ?? 0;
        if (left > 0) remaining.set(id, left - 1);
        return { value: valueText(item), side: left > 0 ? 'both' : 'added' };
    });
    for (const item of before) {
        const id = chipIdentity(item);
        const left = remaining.get(id) ?? 0;
        if (left > 0) {
            remaining.set(id, left - 1);
            items.push({ value: valueText(item), side: 'removed' });
        }
    }
    return { kind: 'chips', path, items };
};

const scalarRow = (path: string[], before: unknown, after: unknown): DiffRow => ({
    kind: 'scalar',
    path,
    before,
    after,
    change: isUnset(before) && !isUnset(after) ? 'added' : isUnset(after) && !isUnset(before) ? 'removed' : 'changed',
});

/** Leaves that differ under `path`. Recurses only into keys and positions that differ. */
const walk = (path: string[], before: unknown, after: unknown): DiffRow[] => {
    if (sameValue(before, after)) return [];
    const absentBefore = before === undefined || before === null;
    const absentAfter = after === undefined || after === null;
    let rows: DiffRow[] = [];

    const beforeList = isScalarList(before) ? before : absentBefore ? [] : null;
    const afterList = isScalarList(after) ? after : absentAfter ? [] : null;
    if (beforeList && afterList && (Array.isArray(before) || Array.isArray(after))) {
        if (beforeList.length || afterList.length) return [chipsRow(path, beforeList, afterList)];
    } else if (Array.isArray(before) || Array.isArray(after)) {
        const b = Array.isArray(before) ? before : absentBefore ? [] : null;
        const a = Array.isArray(after) ? after : absentAfter ? [] : null;
        if (b && a) {
            const length = Math.max(b.length, a.length);
            for (let i = 0; i < length; i++) rows.push(...walk([...path, `#${i + 1}`], b[i], a[i]));
        }
    } else if ((isPlainObject(before) || absentBefore) && (isPlainObject(after) || absentAfter)) {
        const b = isPlainObject(before) ? before : {};
        const a = isPlainObject(after) ? after : {};
        for (const key of unionKeys(b, a)) rows.push(...walk([...path, key], b[key], a[key]));
    }

    if (rows.length === 0) rows = [scalarRow(path, before, after)];
    return rows;
};

const byFieldOrder = (type: EntityType) => (a: Pair, b: Pair) => fieldOrder(type, a.field) - fieldOrder(type, b.field);

const group = (type: EntityType, field: string, rows: DiffRow[]): DiffGroup => {
    const spec = fieldSpec(type, field);
    return { field, label: spec.label, kind: spec.kind, rows };
};

/** The fields that differ, in registry order, each walked to the leaves that differ. */
export const diffGroups = (type: EntityType, pairs: Pair[]): DiffGroup[] =>
    [...pairs]
        .sort(byFieldOrder(type))
        .map((pair) => group(type, pair.field, walk([fieldSpec(type, pair.field).label], pair.before, pair.after)))
        .filter((g) => g.rows.length > 0);

/**
 * Every field the type's registry lists, paired from two whole records — the drawer's "All
 * fields" view and the master panel's full record. A side that is null/undefined pairs as
 * absent for every field, same as a missing record does today.
 */
export const fieldPairs = (
    type: EntityType,
    before: Record<string, unknown> | null | undefined,
    after: Record<string, unknown> | null | undefined,
): Pair[] =>
    Object.keys(FIELDS[type] ?? {}).map((field) => ({ field, before: before?.[field], after: after?.[field] }));

/**
 * Every field, changed or not, both sides shown. A field both sides agree on is still a
 * group, with one row carrying that shared value (dimmed before, plain after) — what used
 * to need a raw JSON view to see. Used by `FieldDiffPanel`'s "All fields" toggle and by
 * `MasterSummary`'s full record (there `after` is master's own base, `before` unused).
 */
export const compareGroups = (type: EntityType, pairs: Pair[]): DiffGroup[] =>
    [...pairs].sort(byFieldOrder(type)).map((pair) => {
        const path = [fieldSpec(type, pair.field).label];
        if (!sameValue(pair.before, pair.after)) return group(type, pair.field, walk(path, pair.before, pair.after));
        const flattened = pair.after === undefined ? [] : walk(path, undefined, pair.after);
        const rows: DiffRow[] = (flattened.length ? flattened : [scalarRow(path, undefined, pair.after)]).map((row) =>
            row.kind === 'scalar'
                ? { ...row, before: row.after, change: 'unchanged' as const }
                : { ...row, items: row.items.map((item) => ({ ...item, side: 'both' as const })) },
        );
        return group(type, pair.field, rows);
    });

/**
 * A whole record, after-only: every field it has, in registry order, flattened the same way. Every row
 * reads as added (before is absent); a field with no leaves (`null`, `{}`, `[]`) is still one row.
 */
export const recordGroups = (type: EntityType, entity: Record<string, unknown>): DiffGroup[] =>
    Object.keys(entity)
        .map((field) => ({ field, before: undefined, after: entity[field] }))
        .sort(byFieldOrder(type))
        .map((pair) => {
            const path = [fieldSpec(type, pair.field).label];
            const rows = pair.after === undefined ? [] : walk(path, undefined, pair.after);
            const added = (rows.length ? rows : [scalarRow(path, undefined, pair.after)]).map((row) =>
                row.kind === 'scalar' ? { ...row, change: 'added' as const } : row,
            );
            return group(type, pair.field, added);
        });
